import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, BookOpen } from 'lucide-react';
const bonuses = [{
  number: '01',
  title: 'First Class Ready',
  subtitle: '30 Done-For-You First Session Scripts for New & Existing Clients',
  desc: 'Never feel lost in a first class again. Thirty completely scripted first sessions for different client profiles — from total beginners to experienced athletes. Includes cueing language, motivational phrases and how to close the session professionally.',
  pages: '80–100 PAGES',
  image: 'https://images.unsplash.com/photo-1574680096145-d05b474e2155?q=80&w=1000&auto=format&fit=crop'
}, {
  number: '02',
  title: 'The Results Tracker',
  subtitle: 'Complete Athlete Monitoring & Progress System',
  desc: "A ready-to-use tracking system to monitor every athlete's progress. Measurement templates, benchmark testing sheets, before and after assessment forms and a simple system to show clients their results visually — boosting retention and referrals.",
  pages: '80–100 PAGES',
  image: 'https://images.unsplash.com/photo-1434493789847-2f02dc6ca35d?q=80&w=1000&auto=format&fit=crop'
}, {
  number: '03',
  title: '365 Post Ideas',
  subtitle: 'A Full Year of Content to Attract Clients on Social Media',
  desc: 'A complete content calendar with 365 post ideas specifically designed for functional and cross training coaches. Organized by category — educational, motivational, promotional and testimonial — so you always know what to post to grow your audience and attract new clients.',
  pages: '80–100 PAGES',
  image: 'https://images.unsplash.com/photo-1611605698335-8b1569810432?q=80&w=1000&auto=format&fit=crop'
}, {
  number: '04',
  title: 'Warm-Up & Cool-Down Vault',
  subtitle: '50 Ready-to-Use Warm-Up and Cool-Down Routines',
  desc: 'A complete library of 50 warm-up and cool-down routines categorized by training focus — strength days, cardio days, mobility days and competition prep. Each routine comes with timing, exercise order and coaching cues. Plug and play for any session.',
  pages: '80–100 PAGES',
  image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?q=80&w=1000&auto=format&fit=crop'
}, {
  number: '05',
  title: 'Client Retention Secrets',
  subtitle: 'The Psychological & Practical Guide to Keeping Clients for 12+ Months',
  desc: 'The business side of coaching that nobody teaches. The psychological triggers behind client dropout and practical communication strategies, check-in systems and motivation frameworks to keep clients engaged and loyal for over a year.',
  pages: '80–100 PAGES',
  image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1000&auto=format&fit=crop'
}];
export function Bonuses() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const toggleExpand = (idx: number) => {
    setExpandedIndex(expandedIndex === idx ? null : idx);
  };
  return <section className="py-12 md:py-24 px-4 bg-brand-dark relative border-t border-zinc-800/50 overflow-hidden" data-id="element-10">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px bg-gradient-to-r from-transparent via-brand-orange to-transparent opacity-50" data-id="element-11"></div>

      <div className="max-w-6xl mx-auto" data-id="element-12">
        <div className="text-center mb-10 md:mb-16" data-id="element-13">
          <div className="inline-flex items-center justify-center p-3 md:p-4 bg-brand-orange/10 rounded-full mb-4 md:mb-6" data-id="element-14">
            <Gift className="w-8 h-8 md:w-10 md:h-10 text-brand-orange" data-id="element-15" />
          </div>
          <h2 className="text-3xl md:text-6xl font-bold mb-4 md:mb-6" data-id="element-16">
            PLUS{' '}
            <span className="text-brand-orange text-glow-orange" data-id="element-17">
              5 FREE BONUSES
            </span>{' '}
            TODAY
          </h2>
          <p className="text-base md:text-xl text-zinc-400 max-w-2xl mx-auto" data-id="element-18">
            We've packed an additional 400+ pages of high-value resources to
            ensure you have absolutely everything you need to succeed.
          </p>
        </div>

        {/* Rows of full-width banners */}
        <div className="relative w-full mb-10 md:mb-12" data-id="element-19">
          <div className="flex flex-col gap-4 md:gap-6" data-id="element-20">
            {bonuses.map((bonus, idx) => <motion.div key={idx} layout onClick={() => toggleExpand(idx)} className="w-full flex flex-col rounded-2xl overflow-hidden cursor-pointer bg-zinc-900 border border-zinc-800 hover:border-brand-orange/50 transition-colors group" data-id="element-21">
                {/* 3:1 Banner Image Area */}
                <motion.div layout="position" className="relative w-full aspect-[2/1] md:aspect-[3/1] bg-zinc-800 overflow-hidden" data-id="element-22">
                  <img src={bonus.image} alt={bonus.title} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700" data-id="element-23" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/20" data-id="element-24"></div>

                  <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end" data-id="element-25">
                    <div className="flex items-end gap-4" data-id="element-26">
                      <span className="text-6xl md:text-8xl font-bold text-brand-orange/20 font-oswald leading-none tracking-tighter" data-id="element-27">
                        {bonus.number}
                      </span>
                      <div className="pb-1 md:pb-2" data-id="element-28">
                        <div className="inline-block px-2 py-1 bg-brand-orange/20 text-brand-orange text-xs font-bold tracking-wider rounded mb-2 backdrop-blur-sm" data-id="element-29">
                          BONUS {idx + 1} — {bonus.pages}
                        </div>
                        <h3 className="text-2xl md:text-4xl font-bold text-white font-oswald tracking-wide" data-id="element-30">
                          {bonus.title}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="absolute top-4 right-4 bg-brand-orange text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider transform rotate-3 shadow-lg" data-id="element-31">
                    FREE TODAY
                  </div>

                  <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-md p-2 rounded-full border border-white/10" data-id="element-32">
                    <motion.div animate={{
                  rotate: expandedIndex === idx ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-33">
                      <ChevronDownIcon className="w-5 h-5 text-white" data-id="element-34" />
                    </motion.div>
                  </div>
                </motion.div>

                {/* Expandable Description */}
                <AnimatePresence data-id="element-35">
                  {expandedIndex === idx && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden bg-zinc-950" data-id="element-36">
                      <div className="p-6 md:p-8 border-t border-zinc-800" data-id="element-37">
                        <h4 className="text-lg md:text-xl font-semibold text-brand-orange mb-4" data-id="element-38">
                          {bonus.subtitle}
                        </h4>
                        <p className="text-zinc-300 text-sm md:text-base leading-relaxed" data-id="element-39">
                          {bonus.desc}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>

        <div className="text-center p-6 md:p-8 rounded-xl bg-gradient-to-r from-brand-orange/10 to-transparent border border-brand-orange/20 max-w-3xl mx-auto" data-id="element-40">
          <p className="text-xl md:text-2xl font-bold text-white" data-id="element-41">
            Total Bonus Value:{' '}
            <span className="line-through text-zinc-500" data-id="element-42">$182</span>{' '}
            <span className="text-brand-orange" data-id="element-43">— TODAY FREE</span>
          </p>
          <p className="text-sm md:text-base text-zinc-400 mt-2" data-id="element-44">
            Included in your system at no extra cost.
          </p>
        </div>
      </div>
    </section>;
}
function ChevronDownIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-45">
      <path d="m6 9 6 6 6-6" data-id="element-46" />
    </svg>;
}